import * as THREE from "three";
import type { FloorplannerStore } from "../store/floorplannerStore";
import { AreaFillSymbolType, RoomType, isAreaFillSymbolType } from "../types/floorplannerTypes";
import { getQuadraticBezierPoint } from "./arcUtils";

export type AreaFillCandidate = {
  roomId: string;
  objectIds?: string[];
  roomSignature?: string;
  vertices: THREE.Vector2[];
  area: number;
  source?: "room" | "geometry";
  trustedRoomGeometry?: boolean;
};

const EPSILON = 1e-9;
const POINT_TOLERANCE = 1e-5;
const GAP_CLOSE_TOLERANCE = 0.3;
const OPENING_CLOSE_MAX_LENGTH = 4;
const OPENING_PARALLEL_DOT = 0.82;
const OPENING_PERPENDICULAR_DOT = 0.35;
const ARC_BOUNDARY_SEGMENTS = 24;

type FallbackAreaFillOptions = {
  closeOpenBoundaries?: boolean;
};

export const arePolygonVerticesClosed = (vertices: THREE.Vector2[]): boolean => {
  if (vertices.length < 2) return false;
  return vertices[0].distanceToSquared(vertices[vertices.length - 1]) < EPSILON;
};

export const closePolygonVertices = (vertices: THREE.Vector2[]): THREE.Vector2[] => {
  if (vertices.length === 0) return [];
  const cloned = vertices.map((vertex) => vertex.clone());
  if (!arePolygonVerticesClosed(cloned)) {
    cloned.push(cloned[0].clone());
  }
  return cloned;
};

export const calculatePolygonArea = (vertices: THREE.Vector2[]): number => {
  const closed = closePolygonVertices(vertices);
  if (closed.length < 4) return 0;

  let sum = 0;
  for (let i = 0; i < closed.length - 1; i++) {
    const current = closed[i];
    const next = closed[i + 1];
    sum += current.x * next.y - next.x * current.y;
  }
  return Math.abs(sum / 2);
};

export const isPointInPolygon = (point: THREE.Vector2, vertices: THREE.Vector2[]): boolean => {
  const closed = closePolygonVertices(vertices);
  if (closed.length < 4) return false;

  let inside = false;
  for (let i = 0, j = closed.length - 2; i < closed.length - 1; j = i++) {
    const xi = closed[i].x;
    const yi = closed[i].y;
    const xj = closed[j].x;
    const yj = closed[j].y;

    const intersects =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi || EPSILON) + xi;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const getSignedPolygonArea = (vertices: THREE.Vector2[]): number => {
  const closed = closePolygonVertices(vertices);
  if (closed.length < 4) return 0;

  let sum = 0;
  for (let i = 0; i < closed.length - 1; i++) {
    const current = closed[i];
    const next = closed[i + 1];
    sum += current.x * next.y - next.x * current.y;
  }

  return sum / 2;
};

const getPolygonCentroid = (vertices: THREE.Vector2[]): THREE.Vector2 => {
  const closed = closePolygonVertices(vertices);
  if (closed.length < 4) {
    return vertices[0]?.clone() ?? new THREE.Vector2();
  }

  let areaFactor = 0;
  let x = 0;
  let y = 0;

  for (let i = 0; i < closed.length - 1; i++) {
    const current = closed[i];
    const next = closed[i + 1];
    const cross = current.x * next.y - next.x * current.y;
    areaFactor += cross;
    x += (current.x + next.x) * cross;
    y += (current.y + next.y) * cross;
  }

  if (Math.abs(areaFactor) < EPSILON) {
    const sum = vertices.reduce(
      (acc, vertex) => acc.add(vertex),
      new THREE.Vector2(),
    );
    return sum.divideScalar(Math.max(vertices.length, 1));
  }

  return new THREE.Vector2(x / (3 * areaFactor), y / (3 * areaFactor));
};

const getObjectSignature = (objectIds: string[]): string => (
  objectIds.slice().sort().join(",")
);

const getRoomAreaFillGeometry = (
  store: FloorplannerStore,
  room: RoomType,
): { vertices: THREE.Vector2[]; trusted: boolean } => {
  const roomFillGeometry = store.getRoomFillGeometry?.(room);
  if (roomFillGeometry) {
    return {
      vertices: closePolygonVertices(roomFillGeometry.vertices),
      trusted: roomFillGeometry.source === "stored",
    };
  }

  const objects = store.getObjectsOfRoom(room);
  return {
    vertices: closePolygonVertices(store.getRoomVertices(objects, "inner")),
    trusted: false,
  };
};

const getPointKey = (point: THREE.Vector2): string => (
  `${Math.round(point.x / POINT_TOLERANCE)},${Math.round(point.y / POINT_TOLERANCE)}`
);

const pointOnSegmentParameter = (
  point: THREE.Vector2,
  start: THREE.Vector2,
  end: THREE.Vector2,
): number | undefined => {
  const segment = end.clone().sub(start);
  const lengthSquared = segment.lengthSq();
  if (lengthSquared < EPSILON) return undefined;

  const t = point.clone().sub(start).dot(segment) / lengthSquared;
  if (t < -POINT_TOLERANCE || t > 1 + POINT_TOLERANCE) return undefined;

  const projected = start.clone().add(segment.multiplyScalar(t));
  if (projected.distanceTo(point) > POINT_TOLERANCE) return undefined;

  return Math.max(0, Math.min(1, t));
};

const segmentIntersection = (
  aStart: THREE.Vector2,
  aEnd: THREE.Vector2,
  bStart: THREE.Vector2,
  bEnd: THREE.Vector2,
): THREE.Vector2 | undefined => {
  const r = aEnd.clone().sub(aStart);
  const s = bEnd.clone().sub(bStart);
  const denominator = r.x * s.y - r.y * s.x;

  if (Math.abs(denominator) < EPSILON) {
    return undefined;
  }

  const delta = bStart.clone().sub(aStart);
  const t = (delta.x * s.y - delta.y * s.x) / denominator;
  const u = (delta.x * r.y - delta.y * r.x) / denominator;

  if (t < -POINT_TOLERANCE || t > 1 + POINT_TOLERANCE || u < -POINT_TOLERANCE || u > 1 + POINT_TOLERANCE) {
    return undefined;
  }

  return aStart.clone().add(r.multiplyScalar(Math.max(0, Math.min(1, t))));
};

const appendBoundarySegments = (
  segments: [THREE.Vector2, THREE.Vector2][],
  boundary: {
    start: THREE.Vector2;
    end: THREE.Vector2;
    lineForm?: "straight" | "arc";
    controlPoint?: THREE.Vector2;
  },
) => {
  if (boundary.start.distanceToSquared(boundary.end) <= EPSILON) return;

  if (boundary.lineForm === "arc" && boundary.controlPoint) {
    let previous = boundary.start.clone();
    for (let i = 1; i <= ARC_BOUNDARY_SEGMENTS; i++) {
      const t = i / ARC_BOUNDARY_SEGMENTS;
      const next = getQuadraticBezierPoint(t, boundary.start, boundary.controlPoint, boundary.end);
      if (previous.distanceToSquared(next) > EPSILON) {
        segments.push([previous, next]);
      }
      previous = next;
    }
    return;
  }

  segments.push([boundary.start.clone(), boundary.end.clone()]);
};

const getWallCenterBoundarySegments = (store: FloorplannerStore): [THREE.Vector2, THREE.Vector2][] => {
  const segments: [THREE.Vector2, THREE.Vector2][] = [];

  store.wallsMap.forEach((wall) => {
    appendBoundarySegments(segments, wall);
  });

  store.singleLinesMap.forEach((line) => {
    appendBoundarySegments(segments, line);
  });

  return segments;
};

const hasEquivalentSegment = (
  segments: [THREE.Vector2, THREE.Vector2][],
  start: THREE.Vector2,
  end: THREE.Vector2,
): boolean => (
  segments.some(([existingStart, existingEnd]) => (
    (existingStart.distanceTo(start) < POINT_TOLERANCE && existingEnd.distanceTo(end) < POINT_TOLERANCE) ||
    (existingStart.distanceTo(end) < POINT_TOLERANCE && existingEnd.distanceTo(start) < POINT_TOLERANCE)
  ))
);

const closestPointOnSegment = (
  point: THREE.Vector2,
  start: THREE.Vector2,
  end: THREE.Vector2,
): THREE.Vector2 | undefined => {
  const segment = end.clone().sub(start);
  const lengthSquared = segment.lengthSq();
  if (lengthSquared < EPSILON) return undefined;

  const t = point.clone().sub(start).dot(segment) / lengthSquared;
  if (t < POINT_TOLERANCE || t > 1 - POINT_TOLERANCE) return undefined;

  return start.clone().add(segment.multiplyScalar(t));
};

const closeBoundaryGaps = (segments: [THREE.Vector2, THREE.Vector2][]): [THREE.Vector2, THREE.Vector2][] => {
  const endpoints = segments.flatMap((segment, segmentIndex) => (
    segment.map((point) => ({ point, segmentIndex }))
  ));
  const additions: [THREE.Vector2, THREE.Vector2][] = [];

  for (let i = 0; i < endpoints.length; i++) {
    let closest: THREE.Vector2 | undefined;
    let closestDistance = GAP_CLOSE_TOLERANCE;

    for (let j = 0; j < endpoints.length; j++) {
      if (i === j) continue;

      const distance = endpoints[i].point.distanceTo(endpoints[j].point);
      if (distance > POINT_TOLERANCE && distance < closestDistance) {
        closest = endpoints[j].point;
        closestDistance = distance;
      }
    }

    if (closest) {
      if (!hasEquivalentSegment(segments.concat(additions), endpoints[i].point, closest)) {
        additions.push([endpoints[i].point.clone(), closest.clone()]);
      }
    }
  }

  endpoints.forEach(({ point, segmentIndex }) => {
    let closestProjection: THREE.Vector2 | undefined;
    let closestDistance = GAP_CLOSE_TOLERANCE;

    segments.forEach(([start, end], candidateIndex) => {
      if (candidateIndex === segmentIndex) return;

      const projection = closestPointOnSegment(point, start, end);
      if (!projection) return;

      const distance = point.distanceTo(projection);
      if (distance > POINT_TOLERANCE && distance < closestDistance) {
        closestProjection = projection;
        closestDistance = distance;
      }
    });

    if (closestProjection && !hasEquivalentSegment(segments.concat(additions), point, closestProjection)) {
      additions.push([point.clone(), closestProjection.clone()]);
    }
  });

  return segments.concat(additions);
};

const closeOpenRoomBoundaries = (segments: [THREE.Vector2, THREE.Vector2][]): [THREE.Vector2, THREE.Vector2][] => {
  type OpenEndpoint = {
    point: THREE.Vector2;
    key: string;
    segmentIndex: number;
    inwardDirection: THREE.Vector2;
  };

  const degreeByKey = new Map<string, number>();
  const pointByKey = new Map<string, THREE.Vector2>();
  const endpointsByKey = new Map<string, { segmentIndex: number; inwardDirection: THREE.Vector2 }[]>();

  segments.forEach(([start, end], segmentIndex) => {
    const startKey = getPointKey(start);
    const endKey = getPointKey(end);
    const direction = end.clone().sub(start);
    if (direction.lengthSq() < EPSILON) return;

    const normalizedDirection = direction.normalize();
    degreeByKey.set(startKey, (degreeByKey.get(startKey) ?? 0) + 1);
    degreeByKey.set(endKey, (degreeByKey.get(endKey) ?? 0) + 1);
    pointByKey.set(startKey, start);
    pointByKey.set(endKey, end);

    if (!endpointsByKey.has(startKey)) endpointsByKey.set(startKey, []);
    if (!endpointsByKey.has(endKey)) endpointsByKey.set(endKey, []);
    endpointsByKey.get(startKey)!.push({ segmentIndex, inwardDirection: normalizedDirection.clone() });
    endpointsByKey.get(endKey)!.push({ segmentIndex, inwardDirection: normalizedDirection.clone().multiplyScalar(-1) });
  });

  const openEndpoints: OpenEndpoint[] = [];
  degreeByKey.forEach((degree, key) => {
    if (degree !== 1) return;
    const endpoint = endpointsByKey.get(key)?.[0];
    const point = pointByKey.get(key);
    if (!endpoint || !point) return;

    openEndpoints.push({
      point,
      key,
      segmentIndex: endpoint.segmentIndex,
      inwardDirection: endpoint.inwardDirection,
    });
  });

  const possibleClosures: { start: OpenEndpoint; end: OpenEndpoint; distance: number }[] = [];
  for (let i = 0; i < openEndpoints.length; i++) {
    for (let j = i + 1; j < openEndpoints.length; j++) {
      const start = openEndpoints[i];
      const end = openEndpoints[j];
      if (start.segmentIndex === end.segmentIndex) continue;

      const closingVector = end.point.clone().sub(start.point);
      const distance = closingVector.length();
      if (distance <= POINT_TOLERANCE || distance > OPENING_CLOSE_MAX_LENGTH) continue;

      const closingDirection = closingVector.normalize();
      const endpointsRunParallel = start.inwardDirection.dot(end.inwardDirection) >= OPENING_PARALLEL_DOT;
      const closingIsAcrossOpening =
        Math.abs(start.inwardDirection.dot(closingDirection)) <= OPENING_PERPENDICULAR_DOT &&
        Math.abs(end.inwardDirection.dot(closingDirection)) <= OPENING_PERPENDICULAR_DOT;

      if (!endpointsRunParallel || !closingIsAcrossOpening) continue;

      possibleClosures.push({ start, end, distance });
    }
  }

  possibleClosures.sort((left, right) => left.distance - right.distance);

  const additions: [THREE.Vector2, THREE.Vector2][] = [];
  const usedEndpointKeys = new Set<string>();
  possibleClosures.forEach(({ start, end }) => {
    if (usedEndpointKeys.has(start.key) || usedEndpointKeys.has(end.key)) return;
    if (hasEquivalentSegment(segments.concat(additions), start.point, end.point)) return;

    additions.push([start.point.clone(), end.point.clone()]);
    usedEndpointKeys.add(start.key);
    usedEndpointKeys.add(end.key);
  });

  return segments.concat(additions);
};

const getOrCreateCanonicalPoint = (
  pointsByKey: Map<string, THREE.Vector2>,
  point: THREE.Vector2,
): THREE.Vector2 => {
  const key = getPointKey(point);
  const existing = pointsByKey.get(key);
  if (existing) return existing;

  const canonical = point.clone();
  pointsByKey.set(key, canonical);
  return canonical;
};

const buildSplitGraph = (
  rawSegments: [THREE.Vector2, THREE.Vector2][],
  options: FallbackAreaFillOptions = {},
) => {
  const pointsByKey = new Map<string, THREE.Vector2>();
  const adjacency = new Map<string, Set<string>>();
  const gapClosedSegments = closeBoundaryGaps(rawSegments);
  const segments = options.closeOpenBoundaries === false
    ? gapClosedSegments
    : closeOpenRoomBoundaries(gapClosedSegments);

  const addEdge = (start: THREE.Vector2, end: THREE.Vector2) => {
    if (start.distanceToSquared(end) < EPSILON) return;

    const canonicalStart = getOrCreateCanonicalPoint(pointsByKey, start);
    const canonicalEnd = getOrCreateCanonicalPoint(pointsByKey, end);
    const startKey = getPointKey(canonicalStart);
    const endKey = getPointKey(canonicalEnd);

    if (startKey === endKey) return;

    if (!adjacency.has(startKey)) adjacency.set(startKey, new Set());
    if (!adjacency.has(endKey)) adjacency.set(endKey, new Set());
    adjacency.get(startKey)!.add(endKey);
    adjacency.get(endKey)!.add(startKey);
  };

  segments.forEach(([start, end], segmentIndex) => {
    const splitPoints: { point: THREE.Vector2; t: number }[] = [
      { point: start, t: 0 },
      { point: end, t: 1 },
    ];

    segments.forEach(([otherStart, otherEnd], otherIndex) => {
      if (segmentIndex === otherIndex) return;

      const intersection = segmentIntersection(start, end, otherStart, otherEnd);
      if (intersection) {
        const t = pointOnSegmentParameter(intersection, start, end);
        if (t !== undefined) {
          splitPoints.push({ point: intersection, t });
        }
      }

      const otherEndpoints = [otherStart, otherEnd];
      otherEndpoints.forEach((endpoint) => {
        const t = pointOnSegmentParameter(endpoint, start, end);
        if (t !== undefined) {
          splitPoints.push({ point: endpoint, t });
        }
      });
    });

    splitPoints.sort((left, right) => left.t - right.t);

    const uniquePoints: THREE.Vector2[] = [];
    splitPoints.forEach(({ point }) => {
      if (!uniquePoints.some((existing) => existing.distanceTo(point) < POINT_TOLERANCE)) {
        uniquePoints.push(point);
      }
    });

    for (let i = 0; i < uniquePoints.length - 1; i++) {
      addEdge(uniquePoints[i], uniquePoints[i + 1]);
    }
  });

  return { pointsByKey, adjacency };
};

const getFallbackAreaFillCandidates = (
  store: FloorplannerStore,
  point?: THREE.Vector2,
  options: FallbackAreaFillOptions = {},
): AreaFillCandidate[] => {
  const rawSegments = getWallCenterBoundarySegments(store);
  if (rawSegments.length < 3) return [];

  const { pointsByKey, adjacency } = buildSplitGraph(rawSegments, options);
  const sortedAdjacency = new Map<string, string[]>();

  adjacency.forEach((neighbors, key) => {
    const origin = pointsByKey.get(key);
    if (!origin) return;

    sortedAdjacency.set(key, Array.from(neighbors).sort((leftKey, rightKey) => {
      const left = pointsByKey.get(leftKey)!;
      const right = pointsByKey.get(rightKey)!;
      const leftAngle = Math.atan2(left.y - origin.y, left.x - origin.x);
      const rightAngle = Math.atan2(right.y - origin.y, right.x - origin.x);
      return leftAngle - rightAngle;
    }));
  });

  const visited = new Set<string>();
  const candidates: AreaFillCandidate[] = [];

  sortedAdjacency.forEach((neighbors, startKey) => {
    neighbors.forEach((nextKey) => {
      const directedStart = `${startKey}->${nextKey}`;
      if (visited.has(directedStart)) return;

      const cycleKeys: string[] = [];
      let fromKey = startKey;
      let toKey = nextKey;

      for (let guard = 0; guard < 1000; guard++) {
        const directedKey = `${fromKey}->${toKey}`;
        if (visited.has(directedKey)) break;

        visited.add(directedKey);
        cycleKeys.push(fromKey);

        const neighborsAtTo = sortedAdjacency.get(toKey);
        if (!neighborsAtTo || neighborsAtTo.length === 0) break;

        const reverseIndex = neighborsAtTo.indexOf(fromKey);
        if (reverseIndex < 0) break;

        const nextIndex = (reverseIndex - 1 + neighborsAtTo.length) % neighborsAtTo.length;
        const followingKey = neighborsAtTo[nextIndex];

        fromKey = toKey;
        toKey = followingKey;

        if (fromKey === startKey && toKey === nextKey) {
          const vertices = closePolygonVertices(cycleKeys.map((key) => pointsByKey.get(key)!.clone()));
          const signedArea = getSignedPolygonArea(vertices);
          const area = Math.abs(signedArea);

          if (
            area > EPSILON &&
            signedArea > 0 &&
            (!point || isPointInPolygon(point, vertices))
          ) {
            candidates.push({
              roomId: "geometry",
              vertices,
              area,
              source: "geometry",
            });
          }
          break;
        }
      }
    });
  });

  return candidates;
};

export const getAreaFillWorldVertices = (areaFill: AreaFillSymbolType): THREE.Vector2[] => (
  areaFill.vertices.map((vertex) => vertex.clone().add(areaFill.position))
);

export const isPointInsideExistingAreaFill = (
  store: FloorplannerStore,
  point: THREE.Vector2,
): boolean => {
  return findAreaFillAtPoint(store, point) !== undefined;
};

/**
 * Smallest room floor fill under a world point, if any.
 * Prefers the tightest polygon so nested fills resolve predictably.
 */
export const findAreaFillAtPoint = (
  store: FloorplannerStore,
  point: THREE.Vector2,
): AreaFillSymbolType | undefined => {
  let best: { fill: AreaFillSymbolType; area: number } | undefined;

  for (const symbol of store.symbolsMap.values()) {
    if (!isAreaFillSymbolType(symbol)) continue;
    const worldVertices = getAreaFillWorldVertices(symbol);
    if (!isPointInPolygon(point, worldVertices)) continue;
    const area = calculatePolygonArea(worldVertices);
    if (!best || area < best.area) {
      best = { fill: symbol, area };
    }
  }

  return best?.fill;
};

export const getAreaFillCandidateAtPoint = (
  store: FloorplannerStore,
  point: THREE.Vector2,
): AreaFillCandidate | undefined => {
  const roomCandidates: AreaFillCandidate[] = [];

  for (const room of store.roomsMap.values()) {
    const { vertices, trusted } = getRoomAreaFillGeometry(store, room);
    const area = calculatePolygonArea(vertices);
    if (area <= EPSILON) continue;
    if (!isPointInPolygon(point, vertices)) continue;

    roomCandidates.push({
      roomId: room.id,
      objectIds: room.objectIds.slice(),
      roomSignature: getObjectSignature(room.objectIds),
      vertices,
      area,
      source: "room",
      trustedRoomGeometry: trusted,
    });
  }

  // First compare persisted rooms only with bounded live faces and short gap
  // repairs. Long synthetic opening closures are intentionally excluded here:
  // in a complex drawing they can connect unrelated endpoints and make the
  // chosen polygon change as the pointer moves across those invented cycles.
  const candidates = roomCandidates.concat(getFallbackAreaFillCandidates(store, point, {
    // Keep the explicit open-sided-room behavior when no persisted room
    // contains the pointer, without letting invented edges override one.
    closeOpenBoundaries: roomCandidates.length === 0,
  }));

  // A persisted room can describe an older, larger boundary while the current
  // wall graph contains smaller compartments. Compare it with live geometry so
  // hover selection resolves to the innermost space at the pointer.
  candidates.sort((left, right) => left.area - right.area);
  return candidates[0];
};

export const getAreaFillCandidates = (
  store: FloorplannerStore,
): AreaFillCandidate[] => {
  const candidates: AreaFillCandidate[] = [];

  for (const room of store.roomsMap.values()) {
    const { vertices, trusted } = getRoomAreaFillGeometry(store, room);
    const area = calculatePolygonArea(vertices);
    if (area <= EPSILON) continue;

    candidates.push({
      roomId: room.id,
      objectIds: room.objectIds.slice(),
      roomSignature: getObjectSignature(room.objectIds),
      vertices,
      area,
      source: "room",
      trustedRoomGeometry: trusted,
    });
  }

  candidates.push(...getFallbackAreaFillCandidates(store));
  candidates.sort((left, right) => left.area - right.area);

  return candidates.filter((candidate, index) => {
    const center = getPolygonCentroid(candidate.vertices);
    return !candidates.some((otherCandidate, otherIndex) => (
      otherIndex < index &&
      otherCandidate.area <= candidate.area &&
      isPointInPolygon(center, otherCandidate.vertices)
    ));
  });
};

export const createAreaFillFromPoint = (
  store: FloorplannerStore,
  point: THREE.Vector2,
): string | undefined => {
  if (isPointInsideExistingAreaFill(store, point)) {
    return undefined;
  }

  const candidate = getAreaFillCandidateAtPoint(store, point);
  if (!candidate) {
    return undefined;
  }

  const origin = candidate.vertices[0].clone();
  const relativeVertices = candidate.vertices.map((vertex) => vertex.clone().sub(origin));
  const roomBinding = candidate.objectIds && candidate.roomSignature
    ? {
      roomId: candidate.roomId,
      roomSignature: candidate.roomSignature,
      objectIds: candidate.objectIds,
      autoCreated: false,
    }
    : undefined;

  return store.addSymbol("areaFill", [origin.x, origin.y], {
    vertices: relativeVertices,
    fillColor: store.fillColor,
    fillPattern: undefined,
    lineColor: store.lineColor,
    lineWeight: store.lineWeight,
    selectable: true,
    ...(roomBinding ? { roomBinding } : {}),
  });
};
